import React, { useState } from 'react';

import { UnfinishedTestCaseExecutionsTableRowProps } from '@qality/common-components';
import Loading from '@qality/common-components/src/atoms/Loading/Loading';
import { IS_CLOUD } from '@qality/common-config';
import { messages } from '@qality/common-messages';
import { Pagination as IPagination, User } from '@qality/common-model';
import {
  PermissionService,
  ProjectService,
  StorageOfUniqueServices,
  UnfinishedTestCaseExecutionsService,
  UserPropertyService,
  UserService,
} from '@qality/common-service';
import TestExecutionReportService from '@qality/common-service/src/TestExecutionReportService';
import { Utils } from '@qality/common-utils';

import { DashboardContext } from './DashboardContext';

const ONBOARDING_GADGET_INFO_PROPERTY = 'ONBOARDING_GADGET_INFO_PROPERTY';

export interface DashboardProviderProps {
  children: React.ReactNode;
}

export const DashboardProvider: React.FC<DashboardProviderProps> = ({
  children,
}: DashboardProviderProps) => {
  const [isQAlityConfigured, setIsQAlityConfigured] = React.useState(false);
  const [isProjectAdmin, setProjectAdmin] = React.useState(false);
  const [projectKey, setProjectKey] = React.useState(null);
  const [projectId, setProjectId] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [
    isLoadingUnfinishedTestCaseExecutions,
    setIsLoadingUnfinishedTestCaseExecutions,
  ] = React.useState(true);
  const [isOnboardingGadgetOpened, setIsOnboardingGadgetOpened] =
    React.useState(false);
  const [
    isOnboardingGadgetClosedManually,
    setIsOnboardingGadgetClosedManually,
  ] = React.useState(false);
  const [user, setUser] = React.useState<User>();
  const [testCaseExecutionReport, setTestCaseExecutionReport] =
    React.useState(null);
  const [showAssignedToMeOnly, setShowAssignedToMeOnly] = useState(true);
  const [unfinishedTestCaseExecutions, setUnfinishedTestCaseExecutions] =
    useState<UnfinishedTestCaseExecutionsTableRowProps[]>([]);
  const [pagination, setPagination] = useState<undefined | IPagination>(
    undefined
  );

  const ITEMS_PER_PAGE = 10;

  const openOnboardingGadget = () => {
    setIsOnboardingGadgetOpened(true);
  };

  const closeOnboardingGadget = () => {
    if (!isOnboardingGadgetClosedManually) {
      setOnboardingGadgetClosedManually(true);
    }
    setIsOnboardingGadgetOpened(false);
  };

  const getProjectKeyAndIdForDashboardFromUrl = async () => {
    const urlSearchParams = await StorageOfUniqueServices.get(
      'URLService'
    ).getJiraURLSearchParams();

    return [
      urlSearchParams.get('project.id'),
      urlSearchParams.get('project.key'),
    ];
  };

  const setOnboardingGadgetClosedManually = async (
    isClosedManually: boolean
  ) => {
    try {
      UserPropertyService.setProperty(
        ONBOARDING_GADGET_INFO_PROPERTY,
        { closedManually: isClosedManually },
        user
      );
    } catch (_e) {
      // do nothing
    }
  };

  const getOnboardingGadgetClosedManually = async (userTmp: User) => {
    try {
      const result = await UserPropertyService.getProperty(
        ONBOARDING_GADGET_INFO_PROPERTY,
        userTmp
      );

      return result.value?.closedManually || false;
    } catch (_e) {
      return false;
    }
  };

  const toggleShowAssignedToMeOnly = async () => {
    const newShowAssignedToMeOnly = !showAssignedToMeOnly;
    setShowAssignedToMeOnly(newShowAssignedToMeOnly);
    UnfinishedTestCaseExecutionsService.setUserProperty(user, {
      showAssignedToMeOnly: newShowAssignedToMeOnly,
    });
    await fetchUnfinishedTestCaseExecutions(0, newShowAssignedToMeOnly);
  };

  const handlePageChange = (_: any, newPage: number) => {
    if (pagination) {
      setPagination({
        ...pagination,
        currentPage: newPage,
        last: newPage === pagination.totalPages - 1,
      });
    }

    fetchUnfinishedTestCaseExecutions(newPage);
  };

  const fetchUnfinishedTestCaseExecutions = async (
    page = 0,
    currentShowAssignedToMeOnly = showAssignedToMeOnly
  ) => {
    try {
      setIsLoadingUnfinishedTestCaseExecutions(true);
      const result =
        await UnfinishedTestCaseExecutionsService.getUnfinishedTestCaseExecutionsData(
          user,
          currentShowAssignedToMeOnly,
          projectId,
          page,
          ITEMS_PER_PAGE
        );
      setPagination(result?.pagination);
      setUnfinishedTestCaseExecutions(result?.data || []);
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.report.loading.next.page.message']
      );
    } finally {
      setIsLoadingUnfinishedTestCaseExecutions(false);
    }
  };

  React.useEffect(() => {
    const init = async () => {
      const [projectIdTmp, projectKeyTmp] =
        await getProjectKeyAndIdForDashboardFromUrl();

      const userTmp = await UserService.getCurrentUser();
      const userPropertyTmp =
        await UnfinishedTestCaseExecutionsService.getUserProperty(userTmp);
      const showAssignedToMeOnlyTmp = !!userPropertyTmp?.showAssignedToMeOnly;

      const [
        isQAlityConfiguredTmp,
        isProjectAdminTmp,
        testCaseExecutionReportTmp,
        isOnboardingGadgetClosedManuallyTmp,
        unfinishedTestCaseExecutionsPageTmp,
      ] =
        projectKeyTmp !== null
          ? await Promise.all([
              ProjectService.isQAlityConfiguredInGivenProject(projectKeyTmp),
              PermissionService.isProjectAdminInGivenProject(projectKeyTmp),
              TestExecutionReportService.getData(0, {
                projectId: projectIdTmp,
              }),
              getOnboardingGadgetClosedManually(userTmp),
              UnfinishedTestCaseExecutionsService.getUnfinishedTestCaseExecutionsData(
                userTmp,
                showAssignedToMeOnlyTmp,
                projectIdTmp,
                0,
                ITEMS_PER_PAGE
              ),
            ])
          : await Promise.all([
              ProjectService.isQAlityConfiguredInAnyProject(),
              PermissionService.isProjectAdminInAnyProject(),
              TestExecutionReportService.getData(0),
              getOnboardingGadgetClosedManually(userTmp),
              UnfinishedTestCaseExecutionsService.getUnfinishedTestCaseExecutionsData(
                userTmp,
                showAssignedToMeOnlyTmp,
                projectIdTmp,
                0,
                ITEMS_PER_PAGE
              ),
            ]);
      const isAnyTestExecutedTmp =
        testCaseExecutionReportTmp.data.rows.content.length > 0;
      const isOnboardingGadgetOpenedTmp = !(
        isOnboardingGadgetClosedManuallyTmp ||
        (isQAlityConfiguredTmp && isAnyTestExecutedTmp)
      );

      await StorageOfUniqueServices.get('URLService').updateUrlParams([
        {
          name: 'projectId',
          value: Utils.isNilOrEmpty(projectIdTmp) ? '' : projectIdTmp,
        },
      ]);

      setProjectKey(projectKeyTmp);
      setProjectId(projectIdTmp);
      setUser(userTmp);
      setShowAssignedToMeOnly(showAssignedToMeOnlyTmp);
      setPagination(unfinishedTestCaseExecutionsPageTmp?.pagination);
      setUnfinishedTestCaseExecutions(
        unfinishedTestCaseExecutionsPageTmp?.data || []
      );
      setTestCaseExecutionReport(testCaseExecutionReportTmp);
      setIsOnboardingGadgetClosedManually(isOnboardingGadgetClosedManuallyTmp);
      setIsOnboardingGadgetOpened(isOnboardingGadgetOpenedTmp);
      setIsQAlityConfigured(isQAlityConfiguredTmp);
      setProjectAdmin(isProjectAdminTmp);
      setIsLoading(false);
      setIsLoadingUnfinishedTestCaseExecutions(false);
    };

    init();
  }, []);

  return (
    <DashboardContext.Provider
      value={{
        isQAlityConfigured,
        isProjectAdmin,
        projectKey,
        projectId,
        user,
        isOnboardingGadgetOpened,
        openOnboardingGadget,
        closeOnboardingGadget,
        testCaseExecutionReport,
        showAssignedToMeOnly,
        pagination,
        isLoading,
        toggleShowAssignedToMeOnly,
        unfinishedTestCaseExecutions,
        handlePageChange,
        isLoadingUnfinishedTestCaseExecutions,
      }}
    >
      {isLoading ? <Loading relative={!IS_CLOUD} /> : children}
    </DashboardContext.Provider>
  );
};
